"use client";

import { useEffect, useState } from "react";

import { useTRPC } from "@/common/utils/trpc";
import {
	Modal,
	Stack,
	Text,
	Button,
	Group,
	Anchor,
	Divider,
} from "@mantine/core";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";

export function UserSMSConsentModal() {
	const [isOpen, setIsOpen] = useState(false);
	const [hasChecked, setHasChecked] = useState(false);

	const trpc = useTRPC();
	const queryClient = useQueryClient();
	const currentUserQuery = useQuery(trpc.users.currentUser.queryOptions());

	const { mutate: confirmConsent, isPending } = useMutation(
		trpc.users.confirmSmsConsent.mutationOptions({
			onSuccess: () => {
				queryClient.invalidateQueries(trpc.users.currentUser.queryFilter());
				setIsOpen(false);
			},
		}),
	);

	const needsConsent =
		currentUserQuery.data &&
		!currentUserQuery.data.smsConsent?.consented &&
		((currentUserQuery.data.type === "user" &&
			currentUserQuery.data.info.phone_number) ||
			(currentUserQuery.data.type === "caregiver" &&
				import.meta.env.VITE_CAPACITOR_BUILD !== "true" &&
				currentUserQuery.data.info.primary_phone_number));

	useEffect(() => {
		if (!hasChecked && currentUserQuery.data) {
			setHasChecked(true);
			if (needsConsent) {
				setIsOpen(true);
			}
		}
	}, [needsConsent, hasChecked, currentUserQuery.data]);

	if (!needsConsent || !currentUserQuery.data) {
		return null;
	}

	return (
		<Modal
			opened={isOpen}
			onClose={() => {}}
			title="SMS Consent"
			size="md"
			closeOnClickOutside={false}
			closeOnEscape={false}
			withCloseButton={false}
			centered
		>
			<Stack gap="md">
				<Text size="sm">
					By clicking <strong>Submit</strong>, you agree to Village Caregiving's{" "}
					<Anchor
						href="/vcg-mobile-app-terms-of-service.html"
						target="_blank"
						size="sm"
						tabIndex={-1}
					>
						Terms of Service
					</Anchor>{" "}
					and{" "}
					<Anchor
						href="/vcg-mobile-app-privacy-policy.html"
						target="_blank"
						size="sm"
						tabIndex={-1}
					>
						Privacy Policy
					</Anchor>
					. You consent to receive SMS messages from Village Caregiving at your
					number on file to provide important information and verification
					codes. Message frequency depends on your activity. You may opt out by
					texting STOP. Text HELP for support. Message and data rates may apply.
				</Text>

				<Divider />

				<Group justify="flex-end">
					<Button
						onClick={() => confirmConsent({ consent: true })}
						loading={isPending}
					>
						Submit
					</Button>
				</Group>
			</Stack>
		</Modal>
	);
}
